import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getProducts, deleteProduct, createProduct, updateProduct, getCategories } from '../services/api';
import apiService from '../services/api';
import Layout from '../components/Layout';
import { showSuccess, showError, showConfirmDanger } from '../services/swal';
import BarcodeLabel from '../components/BarcodeLabel';

const Inventory = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Filters
    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [priceRange, setPriceRange] = useState({ min: '', max: '' });
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [showBarcodeModal, setShowBarcodeModal] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [selectedBarcodeProduct, setSelectedBarcodeProduct] = useState(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [duplicateAction, setDuplicateAction] = useState('skip');
    const [importLoading, setImportLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '', sku: '', unit_price: '', cost_price: '', quantity: '', low_stock_threshold: 10, category_id: ''
    });
    const [categories, setCategories] = useState([]);
    const [importReport, setImportReport] = useState(null);
    const [showImportReportModal, setShowImportReportModal] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [importStats, setImportStats] = useState({ current: 0, total: 0 });

    const handleExport = async () => {
        setExportLoading(true);
        try {
            const res = await apiService.get('/products/export', { responseType: 'blob' });
            const blob = new Blob([res.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `inventory_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            showSuccess('Export Complete', 'Inventory data has been downloaded.');
        } catch (err) {
            showError('Export Failed', 'Could not generate export file. Please try again.');
        } finally {
            setExportLoading(false);
        }
    };

    const downloadTemplate = () => {
        const headers = ['Name', 'SKU', 'Unit Price', 'Cost Price', 'Quantity', 'Category'];
        const sampleData = ['iPhone 15 Pro', 'IPH-15-P', '1200', '1000', '50', 'Electronics'];
        const csvContent = [headers, sampleData].map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'product_import_template.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getProducts({
                search,
                page,
                status: statusFilter,
                category_id: categoryFilter,
                min_price: priceRange.min,
                max_price: priceRange.max,
                sort_by: sortBy,
                sort_order: sortOrder
            });
            setProducts(res.data.data || []);
            setTotalPages(res.data.last_page || 1);
            setTotalCount(res.data.total || 0);
        } catch (err) {
            console.error("Failed to fetch products", err);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, [search, page, statusFilter, categoryFilter, priceRange, sortBy, sortOrder]);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await getCategories();
            setCategories(res.data || []);
        } catch (err) {
            console.error("Failed to fetch categories", err);
        }
    }, []);

    useEffect(() => {
        const debounce = setTimeout(() => {
            fetchProducts();
        }, 300);
        return () => clearTimeout(debounce);
    }, [fetchProducts]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // Calculate Summary Stats
    const summary = useMemo(() => {
        const stats = {
            totalItems: totalCount,
            lowStock: 0,
            outOfStock: 0,
            totalValue: 0
        };
        products.forEach(p => {
            if (p.quantity === 0) stats.outOfStock++;
            else if (p.quantity <= (p.low_stock_threshold || 10)) stats.lowStock++;
            stats.totalValue += (parseFloat(p.unit_price) * p.quantity);
        });
        return stats;
    }, [products]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    const getSortIcon = (field) => {
        if (sortBy !== field) return <i className="bi bi-arrow-down-up text-muted ms-1" style={{ fontSize: '0.8rem' }}></i>;
        return sortOrder === 'asc' ? <i className="bi bi-sort-up text-primary ms-1"></i> : <i className="bi bi-sort-down text-primary ms-1"></i>;
    };

    const handleDelete = async (id) => {
        const result = await showConfirmDanger('Delete Product?', 'This action cannot be undone.');
        if (result.isConfirmed) {
            try {
                await deleteProduct(id);
                fetchProducts();
                showSuccess('Deleted!', 'Product has been removed.');
            } catch (err) {
                showError('Delete Failed', err.response?.data?.message || err.message);
            }
        }
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.name,
            sku: product.sku,
            unit_price: product.unit_price,
            cost_price: product.cost_price,
            quantity: product.quantity,
            low_stock_threshold: product.low_stock_threshold,
            category_id: product.category_id
        });
        setShowModal(true);
    };

    const handleAddNew = () => {
        setEditingProduct(null);
        setFormData({
            name: '', sku: '', unit_price: '', cost_price: '', quantity: '', low_stock_threshold: 10, category_id: ''
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        try {
            if (editingProduct) {
                await updateProduct(editingProduct.id, formData);
                showSuccess('Updated!', 'Product has been updated.');
            } else {
                await createProduct(formData);
                showSuccess('Created!', 'New product has been added.');
            }
            setShowModal(false);
            fetchProducts();
        } catch (err) {
            showError('Save Failed', err.response?.data?.message || err.message);
        }
    };

    const getStatusBadge = (quantity, threshold = 10) => {
        if (quantity === 0) return <span className="badge bg-danger bg-opacity-10 text-danger px-3 py-2 rounded-pill">Out of Stock</span>;
        if (quantity <= threshold) return <span className="badge bg-warning bg-opacity-10 text-warning px-3 py-2 rounded-pill">Low Stock</span>;
        return <span className="badge bg-success bg-opacity-10 text-success px-3 py-2 rounded-pill">In Stock</span>;
    };

    const handleImport = async (e) => {
        e.preventDefault();
        if (!importFile) return;

        setImportLoading(true);
        setImportProgress(0);
        setImportReport(null);

        const reader = new FileReader();
        reader.onload = async (event) => {
            const csvData = event.target.result;
            const lines = csvData.split(/\r?\n/).filter(line => line.trim() !== "");
            if (lines.length <= 1) {
                showError('Empty File', 'The CSV file has no data.');
                setImportLoading(false);
                return;
            }

            const header = lines[0];
            const rows = lines.slice(1);
            const totalRows = rows.length;
            setImportStats({ current: 0, total: totalRows });

            const chunkSize = 100;
            let finalStats = { imported: 0, updated: 0, skipped: 0, errors: 0, total_rows: 0 };
            let allErrors = [];

            for (let i = 0; i < rows.length; i += chunkSize) {
                const chunkRows = rows.slice(i, i + chunkSize);
                const chunkCsv = [header, ...chunkRows].join("\n");
                
                const formData = new FormData();
                const blob = new Blob([chunkCsv], { type: 'text/csv' });
                formData.append('file', blob, 'chunk.csv');
                formData.append('duplicate_action', duplicateAction);

                try {
                    const res = await apiService.post('/import/products', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    const report = res.data;
                    
                    finalStats.imported += report.stats.imported;
                    finalStats.updated += report.stats.updated;
                    finalStats.skipped += report.stats.skipped;
                    finalStats.errors += report.stats.errors;
                    finalStats.total_rows += report.stats.total_rows;
                    
                    if (report.error_details) {
                        const adjustedErrors = report.error_details.map(err => ({
                            ...err,
                            row: err.row + i
                        }));
                        allErrors = [...allErrors, ...adjustedErrors];
                    }

                    const progress = Math.min(Math.round(((i + chunkRows.length) / totalRows) * 100), 100);
                    setImportProgress(progress);
                    setImportStats(prev => ({ ...prev, current: Math.min(i + chunkRows.length, totalRows) }));

                } catch (err) {
                    console.error('Chunk import failed:', err);
                }
            }

            setImportReport({ stats: finalStats, error_details: allErrors });
            setShowImportModal(false);
            setShowImportReportModal(true);
            setImportFile(null);
            setImportLoading(false);
            fetchProducts();
        };
        reader.readAsText(importFile);
    };

    const exportImportReportCSV = () => {
        if (!importReport || !importReport.error_details.length) return;
        const headers = ["Row", "Name", "SKU", "Reason"];
        const rows = importReport.error_details.map(e => [e.row, `"${e.name || ''}"`, `"${e.sku || ''}"`, `"${e.error}"`]);
        const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `product_import_errors_${new Date().getTime()}.csv`;
        a.click();
    };

    const exportImportReportPDF = () => {
        const printContent = `
            <html>
                <head>
                    <title>Product Import Failure Report</title>
                    <style>
                        body { font-family: 'Inter', sans-serif; padding: 40px; color: #1e1e2d; }
                        h1 { color: #f1416c; border-bottom: 2px solid #eff2f5; padding-bottom: 15px; font-weight: 800; }
                        .stats { display: flex; gap: 20px; margin: 30px 0; }
                        .stat-box { flex: 1; padding: 20px; border-radius: 12px; background: #f9f9f9; text-align: center; border: 1px solid #eff2f5; }
                        .stat-val { font-size: 28px; font-weight: 800; margin-bottom: 5px; }
                        .stat-label { font-size: 12px; font-weight: 700; text-transform: uppercase; color: #a1a5b7; }
                        table { width: 100%; border-collapse: collapse; margin-top: 30px; }
                        th, td { border: 1px solid #eff2f5; padding: 15px; text-align: left; }
                        th { background: #f5f8fa; font-weight: 700; color: #5e6278; font-size: 13px; }
                        .row-num { font-weight: 800; color: #7e8299; width: 60px; }
                    </style>
                </head>
                <body>
                    <h1>Product Import Failure Report</h1>
                    <p style="color: #a1a5b7; font-weight: 600;">Generated on: ${new Date().toLocaleString()}</p>
                    <div class="stats">
                        <div class="stat-box"><div class="stat-val" style="color: #50cd89">${importReport.stats.imported}</div><div class="stat-label">Imported</div></div>
                        <div class="stat-box"><div class="stat-val" style="color: #009ef7">${importReport.stats.updated}</div><div class="stat-label">Updated</div></div>
                        <div class="stat-box"><div class="stat-val" style="color: #ffc700">${importReport.stats.skipped}</div><div class="stat-label">Skipped</div></div>
                        <div class="stat-box"><div class="stat-val" style="color: #f1416c">${importReport.stats.errors}</div><div class="stat-label">Errors</div></div>
                    </div>
                    <table>
                        <thead><tr><th>Row</th><th>Product Name</th><th>SKU</th><th>Failure Reason</th></tr></thead>
                        <tbody>
                            ${importReport.error_details.map(e => `
                                <tr>
                                    <td class="row-num">#${e.row}</td>
                                    <td>${e.name || '—'}</td>
                                    <td>${e.sku || '—'}</td>
                                    <td style="color: #f1416c; font-weight: 600;">${e.error}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #eff2f5; text-align: center; color: #a1a5b7; font-size: 11px; font-weight: 600;">
                        System Report • appy.pk POS
                    </div>
                </body>
            </html>
        `;
        const win = window.open('', '_blank');
        win.document.write(printContent);
        win.document.close();
        win.print();
    };

    return (
        <Layout title="Inventory Management">
            {/* Summary Dashboard */}
            <div className="row g-4 mb-4">
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="inventory-stat-card">
                        <div className="icon-box bg-primary bg-opacity-10 text-primary"><i className="bi bi-box-seam"></i></div>
                        <div>
                            <small className="text-muted d-block fw-bold mb-1">Total Products</small>
                            <h4 className="fw-bold mb-0">{summary.totalItems}</h4>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="inventory-stat-card">
                        <div className="icon-box bg-warning bg-opacity-10 text-warning"><i className="bi bi-exclamation-triangle"></i></div>
                        <div>
                            <small className="text-muted d-block fw-bold mb-1">Low Stock Alerts</small>
                            <h4 className="fw-bold mb-0 text-warning">{summary.lowStock}</h4>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="inventory-stat-card">
                        <div className="icon-box bg-danger bg-opacity-10 text-danger"><i className="bi bi-x-circle"></i></div>
                        <div>
                            <small className="text-muted d-block fw-bold mb-1">Out of Stock</small>
                            <h4 className="fw-bold mb-0 text-danger">{summary.outOfStock}</h4>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="inventory-stat-card">
                        <div className="icon-box bg-success bg-opacity-10 text-success"><i className="bi bi-currency-dollar"></i></div>
                        <div>
                            <small className="text-muted d-block fw-bold mb-1">Stock Value</small>
                            <h4 className="fw-bold mb-0">Rs. {summary.totalValue.toLocaleString()}</h4>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="inventory-filter-bar shadow-sm bg-white p-4">
                {/* Row 1: Search & Management Buttons */}
                <div className="row g-3 mb-3 pb-3 border-bottom align-items-end">
                    <div className="col-12 col-lg-5 col-xl-4">
                        <div className="search-wrapper position-relative">
                            <i className="bi bi-search search-icon" style={{ position: 'absolute', top: '50%', left: '1.25rem', transform: 'translateY(-50%)', zIndex: 10, color: '#6c757d' }}></i>
                            <input
                                type="text"
                                className="form-control pos-search-input"
                                placeholder="Search inventory by name or SKU..."
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                style={{ height: '52px', paddingLeft: '3.5rem', borderRadius: '14px' }}
                            />
                        </div>
                    </div>
                    <div className="col-12 col-lg-7 col-xl-8">
                        <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                            <button className="btn btn-primary px-4 rounded-3 fw-bold d-flex align-items-center flex-grow-1 flex-md-grow-0" onClick={handleAddNew} style={{ height: '52px' }}>
                                <i className="bi bi-plus-lg me-2"></i> Add Product
                            </button>
                            <button className="btn btn-outline-secondary rounded-3 px-3 d-flex align-items-center flex-grow-1 flex-md-grow-0" onClick={() => setShowImportModal(true)} style={{ height: '52px' }} title="Import CSV">
                                <i className="bi bi-cloud-arrow-up-fill fs-5 me-2"></i> Import
                            </button>
                            <button className="btn btn-outline-secondary rounded-3 px-3 d-flex align-items-center flex-grow-1 flex-md-grow-0" onClick={handleExport} disabled={exportLoading} style={{ height: '52px' }} title="Export CSV">
                                {exportLoading ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-cloud-arrow-down-fill fs-5 me-2"></i>} Export
                            </button>
                        </div>
                    </div>
                </div>

                {/* Row 2: Granular Filters */}
                <div className="row g-3">
                    <div className="col-12 col-sm-6 col-lg-3">
                        <label className="form-label small fw-bold text-muted mb-1">Stock Status</label>
                        <select className="form-select rounded-3 border bg-light bg-opacity-50 w-100" style={{ height: '45px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="">All Availability</option>
                            <option value="in_stock">In Stock</option>
                            <option value="low_stock">Low Stock</option>
                            <option value="out_of_stock">Out of Stock</option>
                        </select>
                    </div>
                    <div className="col-12 col-sm-6 col-lg-3">
                        <label className="form-label small fw-bold text-muted mb-1">Category</label>
                        <select className="form-select rounded-3 border bg-light bg-opacity-50 w-100" style={{ height: '45px' }} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                            <option value="">All Categories</option>
                            {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                        </select>
                    </div>
                    <div className="col-12 col-lg-6">
                        <label className="form-label small fw-bold text-muted mb-1">Price Range (Rs.)</label>
                        <div className="d-flex gap-2">
                            <input type="number" className="form-control rounded-3 border bg-light bg-opacity-50" style={{ height: '45px' }} placeholder="Min" value={priceRange.min} onChange={e => setPriceRange({ ...priceRange, min: e.target.value })} />
                            <input type="number" className="form-control rounded-3 border bg-light bg-opacity-50" style={{ height: '45px' }} placeholder="Max" value={priceRange.max} onChange={e => setPriceRange({ ...priceRange, max: e.target.value })} />
                            <button className="btn btn-light border rounded-3 d-flex align-items-center justify-content-center" onClick={() => { setSearch(''); setStatusFilter(''); setCategoryFilter(''); setPriceRange({ min: '', max: '' }); }} style={{ height: '45px', width: '45px', flexShrink: 0 }} title="Clear All Filters">
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Desktop Table View */}
            <div className="d-none d-lg-block">
                {loading ? (
                    <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                ) : products.length > 0 ? (
                    <div className="table-responsive">
                        <table className="table inventory-table align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase fw-bold">
                                    <th className="cursor-pointer" onClick={() => handleSort('name')}>Product {getSortIcon('name')}</th>
                                    <th className="cursor-pointer" onClick={() => handleSort('sku')}>SKU {getSortIcon('sku')}</th>
                                    <th>Category</th>
                                    <th className="cursor-pointer" onClick={() => handleSort('unit_price')}>Price {getSortIcon('unit_price')}</th>
                                    <th className="cursor-pointer" onClick={() => handleSort('quantity')}>Stock {getSortIcon('quantity')}</th>
                                    <th>Status</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map(p => (
                                    <tr key={p.id}>
                                        <td>
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="bg-primary bg-opacity-10 text-primary rounded-3 p-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}><i className="bi bi-box"></i></div>
                                                <div className="fw-bold">{p.name}</div>
                                            </div>
                                        </td>
                                        <td className="text-muted small">{p.sku}</td>
                                        <td><span className="text-muted small bg-light px-2 py-1 rounded">{categories.find(c => c.id === p.category_id)?.name || 'General'}</span></td>
                                        <td className="fw-bold">Rs. {parseFloat(p.unit_price).toFixed(2)}</td>
                                        <td>{p.quantity}</td>
                                        <td>{getStatusBadge(p.quantity, p.low_stock_threshold)}</td>
                                        <td className="text-end">
                                            <div className="dropdown">
                                                <button className="btn btn-light btn-sm rounded-circle shadow-sm" data-bs-toggle="dropdown" style={{ width: '32px', height: '32px' }}><i className="bi bi-three-dots-vertical"></i></button>
                                                <ul className="dropdown-menu dropdown-menu-end border-0 shadow-lg rounded-3">
                                                    <li><button className="dropdown-item py-2" onClick={() => handleEdit(p)}><i className="bi bi-pencil-square me-2 text-primary"></i> Edit Details</button></li>
                                                    <li><button className="dropdown-item py-2" onClick={() => { setSelectedBarcodeProduct(p); setShowBarcodeModal(true); }}><i className="bi bi-qr-code me-2 text-info"></i> Generate Barcode</button></li>
                                                    <li><hr className="dropdown-divider" /></li>
                                                    <li><button className="dropdown-item py-2 text-danger" onClick={() => handleDelete(p.id)}><i className="bi bi-trash me-2"></i> Delete Product</button></li>
                                                </ul>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-5 rounded-4 border empty-state-card">
                        <i className="bi bi-inbox fs-1 opacity-25"></i>
                        <h5 className="mt-3">No products found</h5>
                    </div>
                )}
            </div>

            {/* Mobile Card View */}
            <div className="d-lg-none">
                {products.map(p => (
                    <div key={p.id} className="mobile-inventory-card">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="d-flex align-items-center gap-3">
                                <div className="bg-primary bg-opacity-10 text-primary rounded-3 p-2"><i className="bi bi-box"></i></div>
                                <div>
                                    <h6 className="fw-bold mb-0">{p.name}</h6>
                                    <small className="text-muted">{p.sku}</small>
                                </div>
                            </div>
                            {getStatusBadge(p.quantity, p.low_stock_threshold)}
                        </div>
                        <div className="row g-2 mb-3">
                            <div className="col-6">
                                <div className="bg-light p-2 rounded-3 text-center">
                                    <small className="text-muted d-block small">Stock</small>
                                    <span className="fw-bold">{p.quantity}</span>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="bg-light p-2 rounded-3 text-center">
                                    <small className="text-muted d-block small">Price</small>
                                    <span className="fw-bold text-primary">Rs. {parseFloat(p.unit_price).toFixed(0)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="card-actions">
                            <button className="btn btn-sm btn-light flex-grow-1 rounded-pill" onClick={() => handleEdit(p)}><i className="bi bi-pencil me-1"></i> Edit</button>
                            <button className="btn btn-sm btn-light flex-grow-1 rounded-pill" onClick={() => { setSelectedBarcodeProduct(p); setShowBarcodeModal(true); }}><i className="bi bi-qr-code me-1"></i> Label</button>
                            <button className="btn btn-sm btn-outline-danger px-3 rounded-pill" onClick={() => handleDelete(p.id)}><i className="bi bi-trash"></i></button>
                        </div>
                    </div>
                ))}
            </div>
            {/* Pagination */}
            <div className="card-footer border-0 bg-white p-4 d-flex flex-wrap justify-content-between align-items-center gap-3">
                <div className="text-muted small fw-bold">
                    <i className="bi bi-info-circle me-1"></i>
                    Showing <span className="text-dark">{(page - 1) * 10 + 1}</span> to <span className="text-dark">{Math.min(page * 10, totalCount)}</span> of <span className="text-dark">{totalCount}</span> products
                </div>
                <nav>
                    <ul className="pagination pagination-sm mb-0 gap-1">
                        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                            <button className="page-link rounded-circle border-0 bg-light text-dark" onClick={() => setPage(page - 1)}><i className="bi bi-chevron-left"></i></button>
                        </li>
                        
                        {[...Array(totalPages)].map((_, i) => {
                            const p = i + 1;
                            if (p === 1 || p === totalPages || (p >= page - 1 && p <= page + 1)) {
                                return (
                                    <li key={p} className={`page-item ${page === p ? 'active' : ''}`}>
                                        <button className={`page-link rounded-circle border-0 ${page === p ? 'btn-primary' : 'bg-light text-dark'}`} onClick={() => setPage(p)}>{p}</button>
                                    </li>
                                );
                            } else if (p === page - 2 || p === page + 2) {
                                return <li key={p} className="page-item disabled"><span className="page-link bg-transparent border-0">...</span></li>;
                            }
                            return null;
                        })}

                        <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link rounded-circle border-0 bg-light text-dark" onClick={() => setPage(page + 1)}><i className="bi bi-chevron-right"></i></button>
                        </li>
                    </ul>
                </nav>
            </div>

            {/* Add/Edit Product Modal */}
            {showModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1100 }}></div>
                    <div className="modal fade show d-block" style={{ zIndex: 1101 }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                                <div className="modal-header border-0 bg-primary text-white p-4">
                                    <h5 className="modal-title fw-bold">{editingProduct ? 'Edit Product Details' : 'Add New Product'}</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <label className="form-label small fw-bold">Product Full Name</label>
                                            <input type="text" className="form-control rounded-3" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., iPhone 15 Pro Max" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">SKU / Barcode</label>
                                            <input type="text" className="form-control rounded-3" value={formData.sku} onChange={e => setFormData({ ...formData, sku: e.target.value })} placeholder="e.g., IPH-15-PRO" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">Initial Quantity</label>
                                            <input type="number" className="form-control rounded-3" value={formData.quantity} onChange={e => setFormData({ ...formData, quantity: e.target.value })} placeholder="0" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold text-success">Selling Price (Rs.)</label>
                                            <input type="number" className="form-control rounded-3 border-success border-opacity-25" value={formData.unit_price} onChange={e => setFormData({ ...formData, unit_price: e.target.value })} placeholder="0.00" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold text-danger">Cost Price (Rs.)</label>
                                            <input type="number" className="form-control rounded-3 border-danger border-opacity-25" value={formData.cost_price} onChange={e => setFormData({ ...formData, cost_price: e.target.value })} placeholder="0.00" />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">Category</label>
                                            <select className="form-select rounded-3" value={formData.category_id} onChange={e => setFormData({ ...formData, category_id: e.target.value })}>
                                                <option value="">Select Category</option>
                                                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">Low Stock Alert at</label>
                                            <input type="number" className="form-control rounded-3" value={formData.low_stock_threshold} onChange={e => setFormData({ ...formData, low_stock_threshold: e.target.value })} />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0 p-4 bg-light">
                                    <button className="btn btn-light px-4 rounded-pill fw-bold" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button className="btn btn-primary px-4 rounded-pill fw-bold" onClick={handleSave}>{editingProduct ? 'Update Product' : 'Add Product'}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Barcode Modal */}
            {showBarcodeModal && selectedBarcodeProduct && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1100 }}></div>
                    <div className="modal fade show d-block" style={{ zIndex: 1101 }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                                <div className="modal-header border-0 p-4 pb-0">
                                    <h5 className="modal-title fw-bold">Product Label</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowBarcodeModal(false)}></button>
                                </div>
                                <div className="modal-body p-4 text-center">
                                    <div className="p-4 rounded-4 border mb-3 barcode-preview-container">
                                        <BarcodeLabel 
                                            value={selectedBarcodeProduct.sku} 
                                            name={selectedBarcodeProduct.name}
                                            price={selectedBarcodeProduct.unit_price}
                                        />
                                    </div>
                                    <div className="d-flex justify-content-center gap-2">
                                        <button className="btn btn-light rounded-pill px-4 fw-bold" onClick={() => setShowBarcodeModal(false)}>Close</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Import Modal */}
            {showImportModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1100 }}></div>
                    <div className="modal fade show d-block" style={{ zIndex: 1101 }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                                <div className="modal-header border-0 p-4 pb-0">
                                    <h5 className="modal-title fw-bold">Bulk Import Products</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowImportModal(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    {importLoading ? (
                                        <div className="py-4 text-center">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="fw-bold text-primary">Importing Data...</span>
                                                <span className="small fw-bold">{importStats.current} / {importStats.total}</span>
                                            </div>
                                            <div className="progress rounded-pill mb-3" style={{ height: '12px' }}>
                                                <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: `${importProgress}%` }}></div>
                                            </div>
                                            <p className="small text-muted mb-0">Please do not close this window while we process your products.</p>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleImport}>
                                            <div className="mb-4">
                                                <label className="form-label small fw-bold">Choose CSV File</label>
                                                <div className="input-group">
                                                    <input type="file" className="form-control rounded-3" accept=".csv" onChange={e => setImportFile(e.target.files[0])} required />
                                                </div>
                                            </div>
                                            <div className="mb-4 p-3 bg-light rounded-4">
                                                <label className="form-label small fw-bold d-block mb-3">On Duplicate SKU Check:</label>
                                                <div className="d-flex gap-4">
                                                    <div className="form-check">
                                                        <input className="form-check-input" type="radio" name="dup" id="skip" checked={duplicateAction === 'skip'} onChange={() => setDuplicateAction('skip')} />
                                                        <label className="form-check-label small fw-bold" htmlFor="skip">Skip Record</label>
                                                    </div>
                                                    <div className="form-check">
                                                        <input className="form-check-input" type="radio" name="dup" id="update" checked={duplicateAction === 'update'} onChange={() => setDuplicateAction('update')} />
                                                        <label className="form-check-label small fw-bold" htmlFor="update">Update Prices</label>
                                                    </div>
                                                </div>
                                            </div>
                                            <button type="submit" className="btn btn-primary w-100 btn-lg rounded-4 fw-bold py-3">
                                                <i className="bi bi-cloud-arrow-up me-2"></i>
                                                Start Import Process
                                            </button>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Import Report Modal */}
            {showImportReportModal && importReport && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1100 }}></div>
                    <div className="modal fade show d-block" style={{ zIndex: 1101 }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                                <div className="modal-header border-0 bg-primary text-white p-4">
                                    <div className="d-flex align-items-center justify-content-between w-100">
                                        <h5 className="modal-title fw-bold mb-0"><i className="bi bi-clipboard-data me-2"></i>Import Report</h5>
                                        <div className="d-flex gap-2">
                                            <button className="btn btn-sm btn-light rounded-pill px-3 fw-bold" onClick={exportImportReportCSV} disabled={!importReport?.error_details?.length}>
                                                <i className="bi bi-file-earmark-spreadsheet me-1"></i> Export Failures (CSV)
                                            </button>
                                            <button className="btn btn-sm btn-light rounded-pill px-3 fw-bold" onClick={exportImportReportPDF}>
                                                <i className="bi bi-file-earmark-pdf me-1"></i> Export PDF Report
                                            </button>
                                            <button type="button" className="btn-close btn-close-white ms-2" onClick={() => setShowImportReportModal(false)}></button>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="row g-3 mb-4">
                                        <div className="col-3">
                                            <div className="text-center p-3 bg-success bg-opacity-10 rounded-4">
                                                <div className="fs-3 fw-bold text-success">{importReport.stats?.imported || 0}</div>
                                                <small className="fw-bold text-muted">Imported</small>
                                            </div>
                                        </div>
                                        <div className="col-3">
                                            <div className="text-center p-3 bg-info bg-opacity-10 rounded-4">
                                                <div className="fs-3 fw-bold text-info">{importReport.stats?.updated || 0}</div>
                                                <small className="fw-bold text-muted">Updated</small>
                                            </div>
                                        </div>
                                        <div className="col-3">
                                            <div className="text-center p-3 bg-warning bg-opacity-10 rounded-4">
                                                <div className="fs-3 fw-bold text-warning">{importReport.stats?.skipped || 0}</div>
                                                <small className="fw-bold text-muted">Skipped</small>
                                            </div>
                                        </div>
                                        <div className="col-3">
                                            <div className="text-center p-3 bg-danger bg-opacity-10 rounded-4">
                                                <div className="fs-3 fw-bold text-danger">{importReport.stats?.errors || 0}</div>
                                                <small className="fw-bold text-muted">Errors</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="alert bg-light border-0 rounded-3 p-3 mb-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="bi bi-info-circle text-primary"></i>
                                            <small className="fw-bold">Total Rows Processed: {importReport.stats?.total_rows || 0}</small>
                                        </div>
                                    </div>
                                    {importReport.error_details && importReport.error_details.length > 0 && (
                                        <div>
                                            <h6 className="fw-bold mb-3"><i className="bi bi-exclamation-triangle text-danger me-2"></i>Error Details</h6>
                                            <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                <table className="table table-sm align-middle mb-0">
                                                    <thead className="bg-light small">
                                                        <tr>
                                                            <th>Row</th>
                                                            <th>Name</th>
                                                            <th>SKU</th>
                                                            <th>Reason</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {importReport.error_details.map((err, idx) => (
                                                            <tr key={idx}>
                                                                <td className="fw-bold">#{err.row}</td>
                                                                <td>{err.name || 'N/A'}</td>
                                                                <td className="text-muted small">{err.sku || 'N/A'}</td>
                                                                <td><span className="badge bg-danger bg-opacity-10 text-danger">{err.error}</span></td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer border-0 p-4 bg-light">
                                    <button className="btn btn-primary px-4 rounded-pill fw-bold" onClick={() => setShowImportReportModal(false)}>Close Report</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <style>{`
                .inventory-stat-card {
                    background: var(--card-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 20px;
                    padding: 20px;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    transition: all 0.3s ease;
                }
                .inventory-stat-card:hover {
                    transform: translateY(-5px);
                    box-shadow: var(--card-shadow);
                }
                .icon-box {
                    width: 50px;
                    height: 50px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.5rem;
                }
                .inventory-filter-bar {
                    border-radius: 24px;
                    margin-bottom: 2rem;
                }
                .inventory-table thead th {
                    border-top: none;
                    padding: 1rem;
                }
                .inventory-table tbody tr {
                    border-bottom: 1px solid var(--border-color);
                    transition: all 0.2s ease;
                }
                .inventory-table tbody tr:hover {
                    background-color: var(--bg-color);
                }
                .mobile-inventory-card {
                    background: var(--card-bg);
                    border-radius: 20px;
                    padding: 20px;
                    margin-bottom: 1rem;
                    border: 1px solid var(--border-color);
                }
                .card-actions {
                    display: flex;
                    gap: 10px;
                    border-top: 1px solid var(--border-color);
                    padding-top: 15px;
                }
                
                /* Dark Mode Overrides */
                [data-bs-theme="dark"] .inventory-filter-bar,
                [data-bs-theme="dark"] .pagination-bar,
                [data-bs-theme="dark"] .empty-state-card {
                    background-color: var(--card-bg) !important;
                    border-color: var(--border-color) !important;
                }
                [data-bs-theme="dark"] .bg-light {
                    background-color: rgba(255,255,255,0.05) !important;
                }
                [data-bs-theme="dark"] .form-select,
                [data-bs-theme="dark"] .form-control {
                    background-color: rgba(255,255,255,0.03) !important;
                    border-color: var(--border-color) !important;
                    color: var(--text-color) !important;
                }
                .barcode-preview-container {
                    background-color: #fff !important;
                    color: #000 !important;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                }
            `}</style>
        </Layout>
    );
};

export default Inventory;
